import * as React from "react";
import { useAppDispatch } from "../../store/hooks";
import { addNotification } from "../../store/notificationDrawerSlice";
import { formSet, formSetValue, formTriggerValidation } from "../../pages/gvc/testNewForm/utils";

export interface UseDropHandlerOptions {
  formId: string;
  fieldName: string;
  actionLabel?: string;
  accept?: string[];
}

export function useDropHandler(options: UseDropHandlerOptions) {
  const { formId, fieldName, actionLabel = "Import", accept } = options;
  const dispatch = useAppDispatch();
  const page = typeof window !== "undefined" ? window.location.pathname : "";

  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounter = React.useRef(0);

  const handleFile = React.useCallback(
    async (file: File) => {
      // Check file extension if accept is specified
      if (accept && accept.length > 0) {
        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        if (!accept.some((a) => a.toLowerCase() === ext)) {
          dispatch(
            addNotification({
              page,
              action: actionLabel,
              success: false,
              message: `Invalid file type. Accepted: ${accept.join(", ")}`,
            })
          );
          return;
        }
      }

      try {
        const content = await file.text();

        if (!content || content.trim().length === 0) {
          dispatch(
            addNotification({
              page,
              action: actionLabel,
              success: false,
              message: "File is empty",
            })
          );
          return;
        }

        formSetValue(formId, fieldName, content);
        formSet(formId, `fieldStates.${fieldName}.modified`, true);
        formTriggerValidation(formId);

        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: true,
            message: "File imported successfully",
            details: { fileName: file.name },
          })
        );
      } catch (e) {
        dispatch(
          addNotification({
            page,
            action: actionLabel,
            success: false,
            message: `Failed to read file: ${(e as Error).message}`,
          })
        );
      }
    },
    [formId, fieldName, actionLabel, accept, dispatch, page]
  );

  const dropHandlers = React.useMemo(
    () => ({
      onDragEnter: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        setIsDragOver(true);
      },
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
      },
      onDragLeave: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
          setIsDragOver(false);
        }
      },
      onDrop: async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
          await handleFile(files[0]);
        }
      },
    }),
    [handleFile]
  );

  return { isDragOver, dropHandlers };
}
